// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * D-RPT-2 Sprint B — the SaaS-admin editor for the report-builder AI's system-prompt TEMPLATE.
 *
 * Edit the instructions the AI follows when it writes report SQL. This is the intro + STRICT RULES
 * only; the selected pharmacy's live schema and any "fix this" context are appended automatically at
 * generation time and are NOT editable here (so the schema injection can never be broken). Saving
 * stores a new version (the change history is kept) and takes effect on the very next "Ask the AI".
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Save, RotateCcw, AlertTriangle, CheckCircle2 } from 'lucide-react'
import * as api from '../../api/client'

export default function ReportAiPromptPage() {
  const navigate = useNavigate()
  const [prompt, setPrompt] = useState('')
  const [defaultPrompt, setDefaultPrompt] = useState('')
  const [isDefault, setIsDefault] = useState<boolean | null>(null)
  const [updatedAt, setUpdatedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    api.getReportAiPrompt()
      .then(r => {
        setPrompt(r.prompt)
        setDefaultPrompt(r.default_prompt)
        setIsDefault(r.is_default)
        setUpdatedAt(r.updated_at)
      })
      .catch(e => setError(friendly(e)))
      .finally(() => setLoading(false))
  }, [])

  async function save() {
    if (!prompt.trim()) { setError('The prompt cannot be empty.'); return }
    setSaving(true); setError(null); setSaved(false)
    try {
      const r = await api.saveReportAiPrompt(prompt)
      setPrompt(r.prompt)
      setIsDefault(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (e: any) {
      setError(friendly(e))
    } finally { setSaving(false) }
  }

  const atDefault = prompt.trim() === defaultPrompt.trim()

  return (
    <div style={{ padding: '18px 22px', maxWidth: 980, margin: '0 auto' }}>
      <button onClick={() => navigate('/admin/reports-ai')} style={backLink}>
        <ChevronLeft size={14} aria-hidden="true" /> AI Report Builder
      </button>

      <h1 style={{ fontSize: 21, margin: '4px 0 4px' }}>AI Prompt</h1>
      <p style={{ margin: '0 0 14px', color: 'var(--color-text-mute)', fontSize: 13 }}>
        The instructions the AI follows when it writes report SQL. You are editing the intro and rules
        only — the selected pharmacy's live schema and any "fix this" context are appended
        automatically at generation time. Saving takes effect on the next "Ask the AI".
      </p>

      {loading ? (
        <p style={{ fontSize: 13, color: 'var(--color-text-mute)' }}>Loading…</p>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, fontSize: 12, color: 'var(--color-text-mute)' }}>
            {isDefault
              ? <span>Currently using the built-in default.</span>
              : <span>Custom prompt in use{updatedAt ? ` · last saved ${new Date(updatedAt).toLocaleString()}` : ''}.</span>}
            <span style={{ marginLeft: 'auto' }}>{prompt.length.toLocaleString()} / 20,000</span>
          </div>

          <textarea
            value={prompt}
            onChange={e => { setPrompt(e.target.value); setSaved(false) }}
            rows={26}
            spellCheck={false}
            aria-label="AI prompt template"
            style={{ display: 'block', width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-surface)', font: 'inherit', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5, lineHeight: 1.55, resize: 'vertical' }}
          />

          {error && (
            <div style={errBox}><AlertTriangle size={15} aria-hidden="true" style={{ flexShrink: 0 }} /> {error}</div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={save} disabled={saving || !prompt.trim()} style={primaryBtn}>
              <Save size={14} aria-hidden="true" /> {saving ? 'Saving…' : 'Save prompt'}
            </button>
            <button onClick={() => { setPrompt(defaultPrompt); setSaved(false) }} disabled={atDefault} style={ghostBtn}>
              <RotateCcw size={14} aria-hidden="true" /> Reset to default
            </button>
            {saved && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--color-primary, #0d9488)' }}>
                <CheckCircle2 size={14} aria-hidden="true" /> Saved — live on the next generation.
              </span>
            )}
            {atDefault && !saved && (
              <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>Matches the built-in default.</span>
            )}
          </div>

          <p style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 12 }}>
            Tip: keep the output rules (raw SQL only, one statement, no DECLARE, ASCII) — the query
            guard depends on them. Every save is versioned and audited.
          </p>
        </>
      )}
    </div>
  )
}

function friendly(e: any): string {
  const body = e?.body
  if (body?.error === 'platform_only') return 'This screen is platform-admin only.'
  if (body?.error === 'prompt_required') return 'The prompt cannot be empty.'
  if (body?.error === 'prompt_too_long') return body.message
  if (body?.error === 'save_failed') return body.message
  return body?.message || e?.message || 'Something went wrong.'
}

const backLink: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', padding: 0, marginBottom: 6, cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 600, color: 'var(--color-text-mute)' }
const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: 'none', borderRadius: 7, background: 'var(--color-primary, #0d9488)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const ghostBtn: React.CSSProperties = { ...primaryBtn, background: 'var(--color-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)' }
const errBox: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, padding: '9px 12px', border: '1px solid var(--color-danger-border, #f0c9c9)', background: 'var(--color-danger-bg, #fdf2f2)', borderRadius: 7, fontSize: 12.5, color: 'var(--color-danger, #b3261e)' }
